<script setup>
import { computed, onMounted, ref } from "vue";
import TagApi from "@api/tag.api";
import FormFileUpload from "@/components/form/FormFileUpload.vue";
import { useMetadataStore } from "@store/metadata";
import HandleGroupSelectData from "@/services/HandleGroupSelectData";

const state = useMetadataStore();
const metadata = computed(() => state.getMetaData);
const options = ref([]);

const documentTypesForSelect = computed(() => {
    const raw = metadata.value.documentTypes ?? [];
    return HandleGroupSelectData(raw) ?? [];
});
const documentTypesForSelectSimple = computed(() =>
    (metadata.value.documentTypes ?? []).slice(0, 2)
);
const offeringForSelect = computed(() => metadata.value.offering ?? []);
const businessDomainsForSelect = computed(
    () => metadata.value.businessDomains ?? []
);
const businessUnitsForSelect = computed(
    () => metadata.value.businessUnit ?? []
);
const usersForSelect = computed(() => metadata.value.users ?? []);
const industriesForSelect = computed(() => metadata.value.industries ?? []);

// Tags from metadata and normalized for select
const tagsFromMeta = computed(() => metadata.value.tags || []);
const tagsForSelect = computed(() => {
    return tagsFromMeta.value
        .filter((tag) => tag && tag.name && typeof tag.name === "string")
        .map((tag) => ({ id: tag.id, name: tag.name }));
});

const props = defineProps({
    document: {
        type: Object,
    },
    formErrors: {
        type: Object,
    },
    v$: {
        type: Object,
    },
    isUpdateForm: {
        type: Boolean,
    },
});

// Inline required validation for files (create mode only)
const filesErrors = computed(() => {
    const errs = Array.isArray(props.formErrors?.files)
        ? [...props.formErrors.files]
        : [];
    const isCreate = !props.document?.id;
    const noFiles =
        !Array.isArray(props.document?.files) ||
        props.document.files.length === 0;
    if (isCreate && noFiles) {
        errs.push("Please upload at least one file.");
    }
    return errs;
});


const onUploadSuccess = (response) => {
    if (!props.document.files) props.document.files = [];

    // Store full file object (not only ID)
    props.document.files = [{
        id: response.file.id,
        name: response.file.name,
        size: response.file.size,
        type: response.file.type,
        uid: response.file.uid || response.file.id,
    }];
};


const onUploadDelete = (file) => {
    // Ensure files array exists
    // if (!props.document.files) {
    //     props.document.files = [];
    //     return;
    // }

    delete props.document.files ;
    // const fileIndex = props.document.files.findIndex(
    //     (fileId) => fileId === file.id
    // );
    // if (fileIndex !== -1) {
    //     props.document.files.splice(fileIndex, 1);
      
    // }
};

const remoteMethod = (query) => {
    if (query) {
        TagApi.list({ Keyword: query, page: 0, limit: 100 }).then((res) => {
            options.value = res.data.data
                .filter((item) => {
                    return item.name
                        .toLowerCase()
                        .includes(query.toLowerCase());
                })
                .map((item) => {
                    return {
                        value: item.id,
                        label: item.name,
                    };
                });
        });
    } else {
        options.value = [];
    }
};

// Simple tag creation function for form-el-select
const createTag = async (tagName) => {
    try {
        // Create the tag in the database (firstOrCreate handles duplicates)
        const response = await TagApi.store({ name: tagName });

        // Add the new tag to the metadata store
        const newTag = {
            id: response.data.data.tag.id,
            name: response.data.data.tag.name,
        };

        // Ensure the newly created tag is selected by default
        if (!Array.isArray(props.document.tags)) {
            props.document.tags = [];
        }

        // If a temporary label was added by the select, replace it with the new ID
        const lastIdx = props.document.tags.lastIndexOf(tagName);
        if (lastIdx !== -1) {
            props.document.tags.splice(lastIdx, 1, newTag.id);
        } else if (!props.document.tags.includes(newTag.id)) {
            props.document.tags.push(newTag.id);
        }

        if (!state.metadata.tags) {
            state.metadata.tags = [];
        }

        const existingTag = state.metadata.tags.find((t) => t.id === newTag.id);

        if (!existingTag) {
            state.metadata.tags.push(newTag);
        }

        return newTag;
    } catch (error) {
        console.error("Error creating tag:", error);
        throw error;
    }
};

onMounted(() => {
    if (!props.isUpdateForm) {
        props.document.tags = [];
    }
    remoteMethod();
});
</script>

<template>
    <div>
        <template v-if="!document.id">
            <div class="row my-2">
                <div class="col-md-12">
                    <label class="form-label"> Upload Files </label>
                    <FormFileUpload
                        :allowed-files="'.jpg, .jpeg, .png, .pdf, .doc, .docx, .xls, .xlsx, .csv, .ppt, .pptx, .mp4, .mov, .vsdx'"
                        :errors="filesErrors"
                        :limit="10"
                        :model="document"
                        name="files"
                        :multiple="false"
                        :required="true"
                        clearable
                        label="Upload Files"
                        type="document_attachment"
                        @successUpload="onUploadSuccess"
                        @successDeleteUpload="onUploadDelete"
                    >
                    </FormFileUpload>
                    <small
                        v-for="error in filesErrors || []"
                        class="text-danger"
                        style="font-size: 0.8rem; top: 40px"
                    >
                        {{ error }}
                    </small>
                </div>
            </div>
        </template>
        <div class="row my-2">
            <div class="col-md-6">
                <form-input
                    id="name"
                     :model="document"
                   v-model="document.title"
                    :errors="v$.title"
                    :label="$t('fields.document_title')"
                    placeholder="What is this document about?"
                    type="text"
                />
            </div>
            <div class="col-md-6">
                <group-form-el-select
                    :errors="formErrors"
                    :label="$t('fields.document_types')"
                    :model="document"
                    :options="documentTypesForSelect"
                    :required="true"
                    clearable
                    label-field="name"
                    group-by-field="group"
                    name="document_type_id"
                    placeholder="Select whether this is a Case Study, EM Guide, CXO etc"
                />
                <small
                    v-for="error in v$.document_type_id.$errors"
                    :key="error.$uid"
                    class="text-danger"
                    style="font-size: 0.8rem; top: 40px"
                >
                    {{ error.$message }}
                </small>
            </div>
            <!--            <div class="col-md-6">-->
            <!--                <form-el-select-->
            <!--                    :errors="formErrors"-->
            <!--                    :label=""-->
            <!--                    :model="document"-->
            <!--                    :options="documentTypesForSelect"-->
            <!--                    :required="true"-->
            <!--                    clearable-->
            <!--                    label-field="fields.document_type_id"-->
            <!--                    name="document_type_id"-->
            <!--                    placeholder="Select whether this is a Case Study, EM Guide, CXO etc"/>-->
            <!--            </div>-->
            <div class="col-md-6 simple-select" v-if="false">
                <div class="form-label">
                    {{ $t("fields.document_type_input") }}
                    <span class="text-danger">*</span>
                </div>

                <div class="d-flex gap-2">
                    <!-- BD Template Option -->
                    <label
                        v-for="doc in documentTypesForSelectSimple"
                        :key="doc.id"
                        :class="{
                            'border-primary':
                                document.document_type_id === doc.id,
                        }"
                        class="flex-grow-1 border rounded d-flex align-items-center justify-content-between px-3 py-3"
                        style="cursor: pointer"
                    >
                        <span class="fontS-14 font-weight-lighter">{{
                            doc.name
                        }}</span>
                        <input
                        :model="document"
                            name="document_type_id"
                            :value="doc.id"
                            class="form-check-input"
                            type="radio"
                        />
                    </label>

                    <!-- Benchmarking Option -->
                    <!-- <label
                        :class="{
                            'border-primary':
                                document.document_type_id === 30,
                        }"
                        class="flex-grow-1 border rounded d-flex align-items-center justify-content-between px-3 py-3"
                        style="cursor: pointer"
                    >
                        <span class="fontS-14 font-weight-lighter">Benchmarking</span>
                        <input
                            v-model="document.document_type_id"
                            :value="30"
                            class="form-check-input"
                            type="radio"
                        />
                    </label> -->
                </div>

                <small
                    v-for="error in v$.document_type_id.$errors"
                    :key="error.$uid"
                    class="text-danger"
                    style="font-size: 0.8rem; top: 40px"
                >
                    {{ error.$message }}
                </small>
            </div>
        </div>
        <div class="row my-2">
            <div class="col-md-6">
                <form-el-select
                    :errors="formErrors"
                    :label="$t('fields.offering_title')"
                    :model="document"
                    :multiple="true"
                    :options="offeringForSelect"
                    :required="true"
                    clearable
                    label-field="fields.offering_id"
                    name="offering_ids"
                    placeholder="Select the Offering that best fits the project"
                />
                <small
                    v-for="error in v$.offering_ids.$errors"
                    :key="error.$uid"
                    class="text-danger"
                    style="font-size: 0.8rem; top: 40px"
                >
                    {{ error.$message }}
                </small>
            </div>
            <div class="col-md-6">
                <form-el-select
                    :errors="formErrors"
                    :label="$t('fields.business_domain')"
                    :model="document"
                    :multiple="true"
                    :options="businessDomainsForSelect"
                    :required="true"
                    clearable
                    label-field="fields.business_domain"
                    name="business_domain_ids"
                    placeholder="Selected base on Service offering"
                />
                <small
                    v-for="error in v$.business_domain_ids.$errors"
                    :key="error.$uid"
                    class="text-danger"
                    style="font-size: 0.8rem; top: 40px"
                >
                    {{ error.$message }}
                </small>
            </div>
        </div>
        <div class="row my-2">
            <div class="col-md-6">
                <form-el-select
                    :errors="formErrors"
                    :label="$t('fields.authors')"
                    :model="document"
                    :multiple="true"
                    :options="usersForSelect"
                    :required="true"
                    clearable
                    label-field="fields.team_ids"
                    name="authors_ids"
                    placeholder="Select the TAMers who worked on creating this document."
                />
                <small
                    v-for="error in v$.authors_ids.$errors"
                    :key="error.$uid"
                    class="text-danger"
                    style="font-size: 0.8rem; top: 40px"
                >
                    {{ error.$message }}
                </small>
            </div>
            <div class="col-md-6">
                <form-el-select
                    :errors="formErrors"
                    :label="$t('fields.business_unit')"
                    :model="document"
                    :options="businessUnitsForSelect"
                    :required="true"
                    clearable
                    label-field="fields.business_unit_id"
                    name="business_unit_id"
                    placeholder="Choose all Business Units involved (e.g., Advisory, Digital)"
                />
                <small
                    v-for="error in v$.business_unit_id.$errors"
                    :key="error.$uid"
                    class="text-danger"
                    style="font-size: 0.8rem; top: 40px"
                >
                    {{ error.$message }}
                </small>
            </div>
        </div>
        <div class="row my-2">
            <div class="col-md-12 mb-3">
                <form-tiptap-text-area
                    id="name"
                   v-model="document.document_context"
                    :errors="v$.document_context"
                    :label="$t('fields.document_context')"
                    :required="true"
                    placeholder="When/why this was created?"
                    rows="4"
                />
            </div>
            <div class="col-md-12 mb-3">
                <form-tiptap-text-area
                    id="name"
                   v-model="document.document_purpose"
                    :errors="v$?.document_purpose"
                    :label="$t('fields.document_purpose')"
                    :required="true"
                    placeholder="How will it help TAMers in future?"
                    rows="4"
                />
            </div>

            <div class="col-md-6">
                <form-el-select
                    :errors="formErrors"
                    :label="$t('fields.industry')"
                    :model="document"
                    :options="industriesForSelect"
                    :required="true"
                    clearable
                    label-field="fields.industry_id"
                    name="industry_id"
                    placeholder="Select the primary sector the document relates to (e.g., Culture, Tourism, Sports, Healthcare, Technology, etc.)"
                />

                <small
                    v-for="error in v$.industry_id.$errors"
                    :key="error.$uid"
                    class="text-danger"
                    style="font-size: 0.8rem; top: 40px"
                >
                    {{ error.$message }}
                </small>
            </div>
            <div class="col-md-6">
                <form-el-select
                    :errors="formErrors"
                    :label="'Tags'"
                    :model="document"
                    :multiple="true"
                    :options="tagsForSelect"
                    :required="false"
                    :allow-create="true"
                    :on-create-tag="createTag"
                    clearable
                    label-field="name"
                    name="tags"
                    placeholder="AI generated Smart Tags"
                />
                <small
                    v-for="(error, index) in formErrors?.tags || []"
                    :key="index"
                    class="text-danger"
                    style="font-size: 0.8rem; top: 40px"
                >
                    {{ error }}
                </small>
            </div>
        </div>
    </div>
</template>
